import { Link } from "react-router-dom";
import { Home, ArrowLeft, Phone, FileText, Users, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/hooks/useLanguage";

const NotFound = () => {
  const { t } = useLanguage();

  const popularPages = [
    { title: "Immigration Services", href: "/practice-areas/immigration-services", icon: Users },
    { title: "Criminal Defense", href: "/practice-areas/criminal-defense", icon: FileText },
    { title: "Family Law", href: "/practice-areas/family-law", icon: Users },
    { title: "Blog & Resources", href: "/blog", icon: BookOpen },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Page Not Found - 404 Error"
        description="The page you're looking for doesn't exist. Browse our immigration, criminal defense, and family law services or contact us directly."
        noindex={true}
      />
      <Header />
      
      <main className="flex-1 flex items-center justify-center bg-gradient-subtle">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-9xl font-bold text-accent mb-4">404</div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Page Not Found</h1>
            <p className="text-xl text-muted-foreground mb-8">
              Sorry, we couldn't find the page you're looking for. The page may have been moved or deleted.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="default">
                <Link to="/">
                  <Home className="mr-2 h-5 w-5" />
                  Go to Homepage
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" onClick={() => window.history.back()}>
                <span className="cursor-pointer">
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Go Back
                </span>
              </Button>
            </div>

            {/* Popular Pages Section */}
            <div className="mt-12 p-6 bg-card rounded-lg shadow-md">
              <h2 className="text-xl font-bold mb-4">Popular Pages</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {popularPages.map((page) => {
                  const Icon = page.icon;
                  return (
                    <Link 
                      key={page.href}
                      to={page.href}
                      className="flex items-center gap-3 p-3 rounded-md border border-border hover:bg-accent/10 hover:border-accent transition-colors"
                    >
                      <Icon className="h-5 w-5 text-accent flex-shrink-0" />
                      <span className="font-medium">{page.title}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Contact Section */}
            <div className="mt-8 p-6 bg-card rounded-lg shadow-md">
              <h2 className="text-xl font-bold mb-3">Need Legal Help?</h2>
              <p className="text-muted-foreground mb-4">
                If you're looking for specific legal services, contact us directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="tel:8329303059" className="flex items-center gap-2 text-accent hover:underline font-semibold">
                  <Phone className="h-5 w-5" />
                  English: (832) 930-3059
                </a>
                <span className="hidden sm:inline">|</span>
                <a href="tel:8323567254" className="flex items-center gap-2 text-accent hover:underline font-semibold">
                  <Phone className="h-5 w-5" />
                  Español: (832) 356-7254
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;
